import type {
  AgentMessageType,
  AgentResponseMessage,
} from "@/features/ai-agent/types/agent";
import type { MessageGenerationStep } from "@/features/ai-agent/types/agent-step";

import { asReplyOutput } from "./agent-step-predicates";

const MEDIA_TYPES: ReadonlyArray<AgentMessageType> = [
  "image",
  "video",
  "audio",
  "file",
];

export const toReplyMediaMessage = (media: {
  type: string;
  url: string;
}): AgentResponseMessage => ({
  type: MEDIA_TYPES.includes(media.type as AgentMessageType)
    ? (media.type as AgentMessageType)
    : "file",
  text: "",
  mediaUrl: media.url,
});

/**
 * The outgoing bubbles a `response` step delivers, in send order - the exact
 * mirror of the backend's `ReplyDraft.fromStep`. `meta.messages` is the split,
 * the model's or the editing teammate's; without one the output rebuilds it:
 * the text bubble first, then one bubble per media. Kept in lockstep with the
 * backend so the optimistic bubble ids line up positionally with the
 * delivered messages.
 */
export const replyBubblesOf = (
  step: MessageGenerationStep,
): AgentResponseMessage[] => {
  const meta = step.meta ?? {};
  const output = asReplyOutput(step);

  if (Array.isArray(meta.messages) && meta.messages.length) {
    return meta.messages.map((m) => ({
      type: MEDIA_TYPES.includes(m.type as AgentMessageType)
        ? (m.type as AgentMessageType)
        : "text",
      text: m.text ?? "",
      mediaUrl: m.mediaUrl,
    }));
  }

  const bubbles: AgentResponseMessage[] = [];

  if (output?.text?.trim()) {
    bubbles.push({ type: "text", text: output.text });
  }
  for (const media of output?.media ?? []) {
    bubbles.push(toReplyMediaMessage(media));
  }

  return bubbles;
};

/** The same bubbles in the same order, surrounding whitespace aside: the
 *  backend's own test of whether a draft went out as written. */
export const sameBubbles = (
  a: AgentResponseMessage[],
  b: AgentResponseMessage[],
): boolean =>
  a.length === b.length &&
  a.every(
    (bubble, i) =>
      bubble.type === b[i].type &&
      bubble.text.trim() === b[i].text.trim() &&
      (bubble.mediaUrl ?? null) === (b[i].mediaUrl ?? null),
  );
